import type { Card } from '../types'
import { hasValue, isBlank, valueSide } from './fieldValue'

// Pure filter/search logic for the mobile deck-management chips (Prompt 8).
// "Not filled" = at least one empty core field (translation, definition,
// example) — exactly the Fill-all working set (ADR-063 §7 Q2 / CORE_FIELDS
// on the backend), so "Not filled" + swipe in the editor walks precisely
// the cards a Fill-all run would touch.

export type CardFilterChip = 'all' | 'no-translation' | 'no-example' | 'not-filled'

/**
 * The card's translation as the server resolved it for this reader.
 *
 * `translation` is now present on every card the API returns, and when it is
 * present it is the whole answer — INCLUDING when it is null. Null means "no
 * translation for you", which on a deck no user can write is a real state: the
 * curated dictionary is the card's content there, and a `Translation` side is
 * a leftover of an earlier seeding pass in whatever language that pass spoke
 * (ADR-050, ADR-063 Amendment 1, ADR-090 Decision 5). Falling back to the side
 * on null is what would show a Russian translation to a French reader.
 *
 * The side is still read when the field is absent entirely — a card object
 * built client-side rather than fetched. That is a different case from null
 * and is deliberately not collapsed into it.
 */
export function cardTranslation(card: Card): string | null {
  if ('translation' in card) return card.translation ?? null
  return valueSide(card.sides, 'translation')?.content ?? null
}

// ADR-104: "has one" means has a value — a non-blank side of the kind, the
// first such in stored order — with the server's blank test. The chip counts
// and Fill ask the same question, so the list under a chip, its count and
// "Select all" name the same cards.
export function hasTranslation(card: Card): boolean {
  return !isBlank(cardTranslation(card))
}
export function hasExample(card: Card): boolean {
  return hasValue(card.sides, 'example')
}
export function hasDefinition(card: Card): boolean {
  return hasValue(card.sides, 'definition')
}

export function cardMatchesFilter(card: Card, chip: CardFilterChip): boolean {
  switch (chip) {
    case 'all': return true
    case 'no-translation': return !hasTranslation(card)
    case 'no-example': return !hasExample(card)
    case 'not-filled': return !hasTranslation(card) || !hasDefinition(card) || !hasExample(card)
  }
}

/**
 * Chip narrowing only — there is deliberately no query half any more.
 *
 * `cardMatchesQuery` used to live here and match a card's term/translation
 * against a typed string. Both of its callers are gone: the mobile feed has
 * searched the SERVER since 2026-08-04 (`useCardSearchInfinite`), and the
 * desktop list stopped filtering client-side in the same pass that built the
 * two-panel editor. It is not kept "in case": a client-side matcher over the
 * pages that happen to be loaded answers "not in this deck" about cards it has
 * never seen, which is the last live member of the ADR-067 Decision 6 family
 * and the exact defect the search endpoint exists to remove. A registered
 * implementation is one caller away from reintroducing it.
 *
 * The chip is a different thing and stays local: it is a persistent narrowing
 * whose deck-wide COUNTS already come from the server (`/cards/counts`), so it
 * never claims to have seen more of the deck than it has.
 */
export function filterCards(cards: Card[], chip: CardFilterChip): Card[] {
  return cards.filter((c) => cardMatchesFilter(c, chip))
}
